import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ComposeTab } from "@/components/email/ComposeTab";
import { SegmentsTab } from "@/components/email/SegmentsTab";
import { HistoryTab } from "@/components/email/HistoryTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Info } from "lucide-react";

const EMPTY_COMPOSE = { name: "", segmentId: "all", subject: "", html: "" };

export default function EmailPage() {
  const [tab, setTab] = useState("compose");
  const [segments, setSegments] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [compose, setCompose] = useState({ ...EMPTY_COMPOSE });
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [sending, setSending] = useState(false);

  const loadSegments = useCallback(() => {
    api.get("/segments").then((r) => setSegments(r.data)).catch(() => {});
  }, []);
  const loadCampaigns = useCallback(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);
  const loadSuppressions = useCallback(() => {
    api.get("/suppressions").then((r) => setSuppressions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadSegments();
    loadCampaigns();
    loadSuppressions();
    api.get("/settings/email-status").then((r) => setEmailStatus(r.data)).catch(() => {});
    api.get("/settings/categories").then((r) => setCategories(r.data.values)).catch(() => {});
  }, [loadSegments, loadCampaigns, loadSuppressions]);

  const anySending = campaigns.some((c) => c.status === "sending");
  useEffect(() => {
    if (!anySending) return undefined;
    const t = setInterval(loadCampaigns, 3000);
    return () => clearInterval(t);
  }, [anySending, loadCampaigns]);

  useEffect(() => {
    api.get("/campaigns/recipient-count", { params: { segment_id: compose.segmentId === "all" ? "" : compose.segmentId } })
      .then((r) => setRecipientInfo(r.data)).catch(() => setRecipientInfo(null));
  }, [compose.segmentId, segments, refreshTick]);

  const sendTest = async () => {
    try {
      const { data } = await api.post("/campaigns/test", { subject: compose.subject, html: compose.html });
      if (data.preview_mode) toast.info(`Test prepared for ${data.to} — preview mode, no real email was sent. Add your Resend key in Settings → Email sending to send for real.`);
      else toast.success(`Test email sent to ${data.to}. Check your inbox.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendCampaign = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/campaigns/send", {
        name: compose.name, subject: compose.subject, html: compose.html,
        segment_id: compose.segmentId === "all" ? null : compose.segmentId,
      });
      if (data.preview_mode) toast.info(`"${compose.name}" prepared for ${data.recipient_count} people in preview mode — no real emails were sent.`);
      else toast.success(`"${compose.name}" is being sent to ${data.recipient_count} people.`);
      setCompose((c) => ({ ...c, name: "", subject: "", html: "" }));
      setTab("history");
      setTimeout(loadCampaigns, 1200);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const onSuppressionsChanged = () => {
    loadSuppressions();
    setRefreshTick((t) => t + 1);
  };

  return (
    <div className="space-y-8" data-testid="email-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Send an Email</h1>
        <p className="text-muted-foreground mt-2">Write once, send to a saved group. Every email includes an unsubscribe link automatically.</p>
      </div>

      {emailStatus && !emailStatus.enabled && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4 text-sm text-amber-900" data-testid="email-preview-banner">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <p><strong>Preview mode:</strong> emails are simulated, nothing is actually sent. Add your Resend API key under <Link to="/settings" className="underline font-medium">Settings → Email sending</Link> to send real email from your domain.</p>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="email-tabs">
          <TabsTrigger value="compose" data-testid="tab-compose">Write an email</TabsTrigger>
          <TabsTrigger value="segments" data-testid="tab-segments">Groups</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Send history</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-6">
          <ComposeTab
            segments={segments} compose={compose} setCompose={setCompose}
            recipientInfo={recipientInfo} sending={sending}
            onSendTest={sendTest} onSend={sendCampaign}
          />
        </TabsContent>

        <TabsContent value="segments" className="mt-6">
          <SegmentsTab
            segments={segments} categories={categories} suppressions={suppressions}
            onSegmentsChanged={loadSegments} onSuppressionsChanged={onSuppressionsChanged}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <HistoryTab campaigns={campaigns} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
